import type { OsFamily, ResourcePath } from '@occ/contracts';
import { useCan } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { AnimatePresence, m } from 'framer-motion';
import {
  Archive,
  ArrowUp,
  Download,
  File as FileIcon,
  FileCode2,
  FileText,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  Link2,
  RefreshCw,
  Trash2,
  UploadCloud,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { describeHttpError, isPendingFeature } from '../api/http';
import {
  breadcrumbOf,
  deleteEntry,
  downloadFile,
  joinPath,
  listDirectory,
  parentOf,
  pathStyleFor,
  rootOf,
  uploadFile,
  type FileEntry,
  type TransferProgress,
} from '../data/files';
import { bytes, timeAgo } from '../lib/format';
import { ErrorNotice } from './ErrorNotice';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Loading,
  Notice,
  Progress,
  Toasts,
  useToasts,
} from './ui';

/**
 * Navegador de archivos remoto (Módulo 6.3, RF-11/RF-12).
 *
 * Las cuatro acciones (`files.list`, `files.download`, `files.upload` y
 * `files.delete`) llegan al nodo. La única que no puede completarse todavía es
 * **subir un archivo del disco del operador**: hace falta una URL firmada donde
 * dejar los bytes antes de que `files.upload` los reclame por su `stagingKey`, y
 * esa ruta no existe. La pantalla lo dice cuando falla, en azul y no en rojo,
 * porque no es un fallo sino una pieza que falta.
 *
 * El estilo de las rutas sale del `osFamily` del nodo: `C:\` y contrabarras en
 * Windows, `/` y barras en el resto. Adivinarlo de lo que conteste el nodo no
 * serviría, porque hay que construir la primera ruta antes de haber preguntado.
 */

/**
 * Icono por tipo de entrada. Para archivos, la extensión sugiere la familia
 * (código, imagen, comprimido) — adivinar el contenido real abriría el archivo,
 * y aquí no hay para qué.
 */
const EXTENSION_ICONS: Record<string, LucideIcon> = {
  png: ImageIcon,
  jpg: ImageIcon,
  jpeg: ImageIcon,
  gif: ImageIcon,
  svg: ImageIcon,
  webp: ImageIcon,
  bmp: ImageIcon,
  zip: Archive,
  rar: Archive,
  '7z': Archive,
  tar: Archive,
  gz: Archive,
  ts: FileCode2,
  tsx: FileCode2,
  js: FileCode2,
  jsx: FileCode2,
  py: FileCode2,
  go: FileCode2,
  rs: FileCode2,
  java: FileCode2,
  c: FileCode2,
  cpp: FileCode2,
  cs: FileCode2,
  ps1: FileCode2,
  sh: FileCode2,
  json: FileCode2,
  yaml: FileCode2,
  yml: FileCode2,
  toml: FileCode2,
  txt: FileText,
  md: FileText,
  log: FileText,
  csv: FileText,
};

function iconFor(entry: FileEntry): LucideIcon {
  if (entry.kind === 'directory') return Folder;
  if (entry.kind === 'symlink') return Link2;
  if (entry.kind === 'other') return FileIcon;
  const extension = entry.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_ICONS[extension] ?? FileIcon;
}

type Transfer = {
  kind: 'subida' | 'bajada';
  name: string;
  progress: TransferProgress;
};

export function FileBrowser({
  nodeId,
  alias,
  online,
  osFamily,
  resourcePath,
}: {
  nodeId: string;
  alias: string;
  online: boolean;
  osFamily: OsFamily | null;
  resourcePath: ResourcePath;
}) {
  const style = pathStyleFor(osFamily);
  const [path, setPath] = useState(() => rootOf(style));
  const [draft, setDraft] = useState(() => rootOf(style));
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [transferError, setTransferError] = useState<unknown>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<FileEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const filePicker = useRef<HTMLInputElement | null>(null);
  /**
   * Ruta del último listado pedido.
   *
   * Dos clics seguidos sobre directorios anidados dejan dos peticiones en vuelo, y
   * el nodo no garantiza que contesten en orden: sin esto, la respuesta lenta de la
   * carpeta anterior pisaría el contenido de la actual y la tabla enseñaría un
   * directorio que no es el de las migas de pan.
   */
  const requested = useRef<string | null>(null);
  const toasts = useToasts();

  const { data: canDownload } = useCan({
    resource: 'actions',
    action: 'files.download',
    params: { path: resourcePath },
  });
  const { data: canUpload } = useCan({
    resource: 'actions',
    action: 'files.upload',
    params: { path: resourcePath },
  });
  const { data: canDelete } = useCan({
    resource: 'actions',
    action: 'files.delete',
    params: { path: resourcePath },
  });

  const load = useCallback(
    async (target: string) => {
      requested.current = target;
      setLoading(true);
      try {
        const listing = await listDirectory(nodeId, target);
        if (requested.current !== target) return;
        setEntries(listing);
        setError(null);
      } catch (cause) {
        if (requested.current !== target) return;
        setEntries([]);
        setError(cause);
      } finally {
        // El indicador solo se apaga si esta petición sigue siendo la vigente: si
        // no, apagarlo diría «ya está» mientras la de verdad todavía viaja.
        if (requested.current === target) setLoading(false);
      }
    },
    [nodeId],
  );

  useEffect(() => {
    setDraft(path);
    if (online) void load(path);
  }, [path, online, load]);

  // El nodo puede cambiar de sistema al reportar su primer inventario: entonces
  // la raíz de la que se partía deja de ser válida.
  useEffect(() => {
    setPath(rootOf(pathStyleFor(osFamily)));
  }, [osFamily]);

  const crumbs = breadcrumbOf(style, path);
  const parent = parentOf(style, path);

  const startDownload = async (entry: FileEntry) => {
    setTransferError(null);
    setTransfer({
      kind: 'bajada',
      name: entry.name,
      progress: { transferredBytes: 0, totalBytes: entry.sizeBytes ?? null },
    });
    try {
      const result = await downloadFile(nodeId, entry.path, (progress) =>
        setTransfer((current) => (current ? { ...current, progress } : current)),
      );
      toasts.ok(
        result.streamed
          ? `${entry.name} descargado (${bytes(result.sizeBytes)}).`
          : `${entry.name}: la descarga se abrió en otra pestaña porque el almacén no permite leerla por trozos.`,
      );
    } catch (cause) {
      setTransferError(cause);
    } finally {
      setTransfer(null);
    }
  };

  const startUpload = async (file: File) => {
    setTransferError(null);
    setTransfer({
      kind: 'subida',
      name: file.name,
      progress: { transferredBytes: 0, totalBytes: file.size },
    });
    try {
      const destinationPath = joinPath(style, path, file.name);
      const { bytesWritten } = await uploadFile(
        nodeId,
        destinationPath,
        file,
        overwrite,
        (progress) => setTransfer((current) => (current ? { ...current, progress } : current)),
      );
      toasts.ok(`${file.name} escrito en el nodo (${bytes(bytesWritten)}).`);
      await load(path);
    } catch (cause) {
      setTransferError(cause);
    } finally {
      setTransfer(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteEntry(nodeId, pendingDelete.path, pendingDelete.kind === 'directory');
      toasts.ok(`${pendingDelete.name} borrado.`);
      setPendingDelete(null);
      await load(path);
    } catch (cause) {
      toasts.fail(describeHttpError(cause));
    } finally {
      setDeleting(false);
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const [file] = Array.from(event.dataTransfer.files);
    // De uno en uno: `files.upload` sube un archivo por llamada y encadenarlos en
    // silencio escondería cuál de los cinco falló.
    if (file) void startUpload(file);
  };

  const columns = useMemo<ColumnDef<FileEntry>[]>(
    () => [
      {
        id: 'nombre',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Nombre" />,
        accessorFn: (entry) => entry.name,
        meta: { label: 'Nombre' },
        cell: ({ row }) => {
          const entry = row.original;
          const Icon = iconFor(entry);
          return entry.kind === 'directory' ? (
            <button
              type="button"
              onClick={() => setPath(entry.path)}
              className="inline-flex items-center gap-2 hover:underline"
            >
              <Icon className="size-4 shrink-0 opacity-70" aria-hidden />
              {entry.name}
            </button>
          ) : (
            <span className="inline-flex items-center gap-2">
              <Icon className="size-4 shrink-0 opacity-60" aria-hidden />
              {entry.name}
            </span>
          );
        },
      },
      {
        id: 'tamano',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Tamaño" />,
        accessorFn: (entry) => (entry.kind === 'directory' ? -1 : (entry.sizeBytes ?? 0)),
        meta: { label: 'Tamaño' },
        cell: ({ row }) => (
          <span className="opacity-70">
            {row.original.kind === 'directory' ? '—' : bytes(row.original.sizeBytes)}
          </span>
        ),
      },
      {
        id: 'modificado',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Modificado" />,
        accessorFn: (entry) => entry.modifiedAt?.getTime() ?? 0,
        meta: { label: 'Modificado' },
        cell: ({ row }) => (
          <span className="opacity-70">
            {row.original.modifiedAt ? timeAgo(row.original.modifiedAt) : '—'}
          </span>
        ),
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => {
          const entry = row.original;
          return (
            <div className="flex justify-end gap-1">
              {entry.kind !== 'directory' && (
                <Button
                  variant="ghost"
                  disabled={!canDownload?.can || transfer !== null}
                  title={canDownload?.can ? 'Descargar' : canDownload?.reason}
                  onClick={() => void startDownload(entry)}
                >
                  <Download className="size-3.5" />
                  Bajar
                </Button>
              )}
              <Button
                variant="danger"
                disabled={!canDelete?.can}
                title={canDelete?.can ? 'Borrar' : canDelete?.reason}
                onClick={() => setPendingDelete(entry)}
              >
                <Trash2 className="size-3.5" />
                Borrar
              </Button>
            </div>
          );
        },
      },
    ],
    // `startDownload` no entra a la lista: es una función nueva en cada
    // render (cierra sobre `path`/`style`, no está en `useCallback`), así que
    // incluirla anularía la memoización de `columns` sin ganar nada —el
    // cierre que usa la celda siempre lee la versión vigente de todas formas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canDownload, canDelete, transfer],
  );

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <FolderOpen className="size-4 opacity-70" />
          Archivos
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button variant="ghost" disabled={!online || loading} onClick={() => void load(path)}>
            <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} />
            Recargar
          </Button>
          <Button
            variant="secondary"
            disabled={!online || !canUpload?.can || transfer !== null}
            title={canUpload?.can ? undefined : canUpload?.reason}
            onClick={() => filePicker.current?.click()}
          >
            <UploadCloud className="size-3.5" />
            Subir archivo
          </Button>
        </div>
      }
    >
      <Toasts toasts={toasts.toasts} />

      <input
        ref={filePicker}
        type="file"
        className="hidden"
        onChange={(event) => {
          const [file] = Array.from(event.target.files ?? []);
          if (file) void startUpload(file);
          // Se limpia para que elegir el mismo archivo dos veces vuelva a disparar
          // el `change`.
          event.target.value = '';
        }}
      />

      <div className="grid gap-3">
        {!online && (
          <Notice tone="warn">
            {alias} no está conectado: el listado se pide al agente en el momento, no hay copia
            guardada.
          </Notice>
        )}

        {/* Migas de pan + salto directo a una ruta */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            disabled={parent === null}
            onClick={() => parent && setPath(parent)}
          >
            <ArrowUp className="size-3.5" />
            Subir
          </Button>
          <nav className="flex flex-wrap items-center gap-1 text-sm">
            {crumbs.map((crumb, index) => (
              <span key={crumb.path} className="inline-flex items-center gap-1">
                {index > 0 && <span className="opacity-30">/</span>}
                <button
                  type="button"
                  onClick={() => setPath(crumb.path)}
                  className="rounded px-1 hover:bg-black/5 dark:hover:bg-white/10"
                >
                  {crumb.label}
                </button>
              </span>
            ))}
          </nav>
        </div>

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setPath(draft.trim() || rootOf(style));
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            spellCheck={false}
            aria-label="Ruta"
            className="min-w-0 flex-1 rounded border border-black/15 dark:border-white/20 bg-transparent px-2.5 py-1.5 font-mono text-[13px] outline-none focus:border-[var(--occ-brand)]"
          />
          <Button type="submit" variant="secondary" disabled={!online}>
            Ir
          </Button>
        </form>

        {error !== null && (
          <ErrorNotice
            error={error}
            what="Listar el directorio"
            pendingHint="Necesita la capacidad «files» en el agente de este nodo."
          />
        )}

        {transferError !== null && (
          <ErrorNotice
            error={transferError}
            what={isPendingFeature(transferError) ? 'Transferir archivos' : 'La transferencia'}
            pendingHint="Falta el staging en S3/MinIO: `files.upload` exige un `stagingKey` que hoy nadie emite."
          />
        )}

        {transfer && (
          <Progress
            value={
              transfer.progress.totalBytes
                ? (transfer.progress.transferredBytes / transfer.progress.totalBytes) * 100
                : undefined
            }
            label={
              <>
                <span>
                  {transfer.kind === 'subida' ? 'Subiendo' : 'Descargando'} {transfer.name}
                </span>
                <span>
                  {bytes(transfer.progress.transferredBytes)}
                  {transfer.progress.totalBytes ? ` / ${bytes(transfer.progress.totalBytes)}` : ''}
                </span>
              </>
            }
          />
        )}

        {/* Zona de arrastrar y soltar: envuelve la tabla, que es donde el técnico
            va a soltar el archivo de forma natural. */}
        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className="relative rounded border border-dashed border-black/10 transition dark:border-white/10"
        >
          <AnimatePresence>
            {dragging && (
              <m.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded bg-[var(--occ-brand)]/10 ring-2 ring-[var(--occ-brand)]"
              >
                <m.div
                  initial={{ scale: 0.9, y: 4 }}
                  animate={{ scale: 1, y: 0 }}
                  className="flex flex-col items-center gap-1.5 text-[var(--occ-brand)]"
                >
                  <UploadCloud className="size-7" />
                  <span className="text-sm font-medium">Suelta para subir a {alias}</span>
                </m.div>
              </m.div>
            )}
          </AnimatePresence>

          {loading && entries.length === 0 ? (
            <div className="p-4">
              <Loading label="Pidiendo el listado al nodo…" />
            </div>
          ) : entries.length === 0 ? (
            <EmptyState title={error !== null ? 'Sin listado' : 'Directorio vacío'}>
              {canUpload?.can
                ? 'Arrastra un archivo aquí para subirlo a esta ruta.'
                : 'No tienes permiso para escribir en este nodo.'}
            </EmptyState>
          ) : (
            <DataTable
              columns={columns}
              data={entries}
              pageSize={0}
              animateRows
              className="p-3"
              emptyMessage="Directorio vacío."
            />
          )}
        </div>

        <Checkbox
          label="Sobreescribir si el archivo ya existe en el destino"
          checked={overwrite}
          onChange={(event) => setOverwrite(event.target.checked)}
        />
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Borrar en el nodo"
        confirmLabel="Borrar"
        busy={deleting}
        // El nombre hay que teclearlo: un borrado en la máquina de un cliente no
        // se deshace, y un directorio se lleva por delante todo lo que cuelga.
        confirmText={pendingDelete?.name}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      >
        <p>
          Se va a borrar de <strong>{alias}</strong>:
        </p>
        <code className="block break-all rounded bg-black/5 dark:bg-white/5 p-2 text-xs">
          {pendingDelete?.path}
        </code>
        {pendingDelete?.kind === 'directory' && (
          <Notice tone="warn">
            Es un directorio: se borra <strong>con todo su contenido</strong> (
            <code>recursive: true</code>).
          </Notice>
        )}
        <p className="opacity-70">
          No hay papelera ni deshacer: el agente ejecuta el borrado en el sistema de archivos del
          nodo. <Badge tone="warn">irreversible</Badge>
        </p>
      </ConfirmDialog>
    </Card>
  );
}
